<template>
  <CstuModal
    id="update-brand-voice-modal"
    title="Update Brand Voice"
    content-class="w-[650px]"
    dialog-class="!max-w-[650px]"
    body-class="no-scrollbar"
    z-index="1020"
    scrollable
    size="lg"
    hide-footer
    centered
    radius='xl'
    no-close-on-backdrop
    @shown="handleShown"
  >

    <div>
      <!-- Steps Progress -->
      <div class="flex justify-center items-center mb-8">
        <template v-for="(step, index) in steps" :key="index">
          <div
            class="flex items-center cursor-pointer"
            @click="handleStepClick(index)"
          >
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center border"
              :class="[
                currentStep === index ? `${step.borderColor}` : 'border-gray-300',
              ]"
            >
              <template
                v-if="completedSteps.includes(index) && currentStep !== index"
              >
                <svg
                  :class="step.textColor"
                  class="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </template>
              <template v-else>
                <img :src="step.icon" :alt="step.name" class="w-5 h-5" />
              </template>
            </div>
            <span
              class="ml-2 text-sm"
              :class="[
                currentStep === index
                  ? `${step.textColor} font-medium`
                  : 'text-gray-900',
              ]"
              >{{ step.name }}</span
            >
          </div>
          <div
            v-if="index < steps.length - 1"
            class="mx-8 w-16 h-px border !border-dashed"
          ></div>
        </template>
      </div>

      <!-- Step Content -->
      <div ref="stepContainer" class="min-h-[300px] 2xl:min-h-[400px]">
        <component :is="steps[currentStep].component" v-model="currentStepData" />
      </div>

      <!-- Navigation Buttons -->
      <div class="flex justify-between mt-6 px-4">
        <Button
          v-if="currentStep > 0"
          class="px-4 py-2 text-[#436AFF] hover:bg-[#F5F7FF] rounded-lg"
          size="sm"
          variant="light"
          color="secondary"
          @click="prevStep"
        >
          Back
        </Button>
        <div class="ml-auto">
          <Button size="sm" :loading="isLoading" @click="handleNextStep">
            {{ currentStep === steps.length - 1 ? 'Update' : 'Next' }}
          </Button>
        </div>
      </div>
    </div>
  </CstuModal>
</template>

<script setup>
import { ref, inject, computed } from 'vue'
import { Button } from '@contentstudio/ui'
import { useSetup } from '@modules/publisher/ai-content-library/composables/useSetup'
import { useBrandInfoSetting } from '@modules/publisher/ai-content-library/composables/useBrandInfoSetting'
import { useIcons } from '@modules/publisher/composables/useIcons'
import StrategyStep from '@modules/publisher/ai-content-library/components/form/steps/StrategyStep.vue'
import TopicsStep from '@modules/publisher/ai-content-library/components/form/steps/TopicsStep.vue'

const { isLoading, brandInfo } = useSetup()
const { handleUpdateBrandVoice } = useBrandInfoSetting()
const { StrategyIcon, TopicsIcon } = useIcons()

const currentStepData = computed(() => {
  switch (currentStep.value) {
    case 0:
      return { strategy: brandInfo.strategy }
    case 1:
      return { topics: brandInfo.topics }
    default:
      return {}
  }
})

const root = inject('root')
const { $cstuModal } = root

const currentStep = ref(0)
const completedSteps = ref([])
const stepContainer = ref(null)

const steps = [
  {
    name: 'Voice',
    component: StrategyStep,
    icon: StrategyIcon,
    textColor: 'text-[#4CCE88]',
    borderColor: '!border-[#4CCE88]',
    color: '#4CCE88',
  },
  {
    name: 'Topics',
    component: TopicsStep,
    icon: TopicsIcon,
    textColor: 'text-[#FF9472]',
    borderColor: '!border-[#FF9472]',
    color: '#FF9472',
  },
]

const handleStepClick = async (index) => {
  if (index === currentStep.value || isLoading.value) return

  if (index > currentStep.value) {
    for (let i = currentStep.value; i < index; i++) {
      if (!completedSteps.value.includes(i)) {
        completedSteps.value.push(i)
      }
    }
  } else if (index < currentStep.value) {
    completedSteps.value = completedSteps.value.filter((step) => step <= index)
  }

  currentStep.value = index
}

const handleNextStep = async () => {
  if (currentStep.value < steps.length - 1) {
    completedSteps.value.push(currentStep.value)
    currentStep.value++
  } else {
    // On final step, save and close
    const result = await handleUpdateBrandVoice()
    if (result) {
      $cstuModal.hide('update-brand-voice-modal')
    }
  }
}

const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

const handleShown = () => {
// Reset step state
  currentStep.value = 0
  completedSteps.value = []
}
</script>
